"use client";

import {
  ArrowDownward,
  ArrowUpward,
  Clear,
  ExpandMore,
  UnfoldMore,
  VisibilityOff,
} from "@mui/icons-material";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import type { Column } from "@tanstack/react-table";
import * as React from "react";
import { useTranslation } from "react-i18next";

type DataTableColumnHeaderProps<TData, TValue> = {
  column: Column<TData, TValue>;
  title: string;
} & React.ComponentProps<typeof Button>;

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  if (!(column.getCanSort() || column.getCanHide())) {
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: "bold",
          fontSize: "0.82rem",
        }}
      >
        {t(`common.table.headers.${title}`)}
      </Typography>
    );
  }

  const sortIcon =
    column.getIsSorted() === "desc" ? (
      <ArrowDownward fontSize="small" />
    ) : column.getIsSorted() === "asc" ? (
      <ArrowUpward fontSize="small" />
    ) : (
      <UnfoldMore fontSize="small" />
    );

  return (
    <>
      <Button
        color="inherit"
        endIcon={column.getCanSort() ? sortIcon : <ExpandMore fontSize="small" />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="medium"
        sx={{
          textTransform: "none",
          textWrap: "nowrap",
          minWidth: 0,
          justifyContent: "flex-start",
          width: "100%",
          fontWeight: "bold",
          px: 0,
        }}
        {...props}
      >
        {t(`common.table.headers.${title}`)}
      </Button>
      <Menu anchorEl={anchorEl} onClose={() => setAnchorEl(null)} open={open}>
        {column.getCanSort()
          ? [
              <MenuItem
                key="sort-asc"
                onClick={() => {
                  column.toggleSorting(false);
                  setAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <ArrowUpward fontSize="small" />
                </ListItemIcon>
                <ListItemText>Asc</ListItemText>
              </MenuItem>,
              <MenuItem
                key="sort-desc"
                onClick={() => {
                  column.toggleSorting(true);
                  setAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <ArrowDownward fontSize="small" />
                </ListItemIcon>
                <ListItemText>Desc</ListItemText>
              </MenuItem>,
              column.getIsSorted() !== false ? (
                <MenuItem
                  key="sort-reset"
                  onClick={() => {
                    column.clearSorting();
                    setAnchorEl(null);
                  }}
                >
                  <ListItemIcon>
                    <Clear fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Reset</ListItemText>
                </MenuItem>
              ) : null,
            ]
          : null}
        {column.getCanHide() && (
          <MenuItem
            onClick={() => {
              column.toggleVisibility(false);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <VisibilityOff fontSize="small" />
            </ListItemIcon>
            <ListItemText>Hide</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
